// SettingsMenu — kleines Zahnrad in der TopBar. Aktuell nur UI-Scale (V3 Slice 3).
// Persistierung in localStorage via theme/ui-scale.ts; Anwendung sofort beim Klick.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  UI_SCALE_VALUES,
  type UiScale,
  applyUiScale,
  loadUiScale,
  saveUiScale,
} from "../theme/ui-scale.js";
import "./SettingsMenu.css";

const SCALE_LABEL_KEYS: Record<UiScale, string> = {
  "1": "settings.uiScale.normal",
  "1.1": "settings.uiScale.comfortable",
  "1.25": "settings.uiScale.large",
};

export function SettingsMenu(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState<UiScale>(() => loadUiScale());
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pickScale = (value: UiScale): void => {
    setScale(value);
    applyUiScale(value);
    saveUiScale(value);
  };

  return (
    <div className="settings-menu" ref={rootRef}>
      <button
        type="button"
        className="settings-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("settings.title")}
        title={t("settings.title")}
        onClick={() => setOpen((p) => !p)}
      >
        ⚙
      </button>
      {open && (
        <div className="settings-menu-popover" role="menu">
          <h5 className="settings-menu-section-title">{t("settings.uiScale")}</h5>
          <ul>
            {UI_SCALE_VALUES.map((value) => (
              <li key={value}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={scale === value}
                  className={`settings-menu-item ${scale === value ? "is-active" : ""}`}
                  onClick={() => pickScale(value)}
                >
                  <span>{t(SCALE_LABEL_KEYS[value])}</span>
                  <span className="settings-menu-item-value">
                    {Math.round(Number(value) * 100)} %
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
